import { noop } from '@packages/core/utils';
import { Button, Typography } from '@packages/ui/shared';
import { Modal } from 'react-bootstrap';

/**
 * Props for the BackModal component.
 */
export type BackModalProps = {
    /** Whether draft editing is allowed (controls the Save button disabled state). */
    canEditDraft: boolean;

    /** Whether a save operation is currently in progress. */
    isSaving: boolean;

    /** Whether the modal is visible. */
    isVisible: boolean;

    /** Callback to dismiss the modal. */
    onClose: () => void;

    /** Callback to exit without saving the draft. */
    onExitWithoutSaving?: () => void;

    /** Callback to save the draft and then exit. */
    onSaveAndExit?: () => void;
};

/**
 * Confirmation modal shown when the user attempts to navigate away from a draft
 * case with unsaved changes. Offers three options: exit without saving, cancel,
 * or save and exit.
 *
 * @param props - component props
 * @returns the back-navigation confirmation modal
 */
export function BackModal({
    canEditDraft,
    isSaving,
    isVisible,
    onClose,
    onExitWithoutSaving = noop,
    onSaveAndExit = noop,
}: BackModalProps) {
    // Forwarded callback aliases
    const handleClose = onClose;
    const handleExitWithoutSavingButtonPress = onExitWithoutSaving;
    const handleSaveAndExitButtonPress = onSaveAndExit;

    return (
        <Modal centered onHide={handleClose} show={isVisible}>
            <Modal.Header>
                <Modal.Title>You have unsaved changes</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Typography>
                    If you exit now, any changes you&apos;ve made since the last save will be lost. What would you like
                    to do?
                </Typography>
            </Modal.Body>

            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <div>
                        <Button onPress={handleExitWithoutSavingButtonPress} variant="ghost-blue">
                            Exit without Saving
                        </Button>
                    </div>
                    <div className="d-flex">
                        <div className="mr-2">
                            <Button onPress={handleClose} variant="ghost-blue">
                                Cancel
                            </Button>
                        </div>
                        <Button
                            disabled={!canEditDraft || isSaving}
                            isLoading={isSaving}
                            onPress={handleSaveAndExitButtonPress}
                            variant="primary"
                        >
                            Save and exit
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
